import { Pipe, PipeTransform } from '@angular/core';
import { CurrencyContextService } from '../services/general/currency-context.service';

/**
 * A money figure, in the BASE currency, as the operator reads it.
 *
 * Two things this pipe used to get wrong, both of which it got wrong on 23 screens at once:
 *
 * 1. **It hard-coded the string ' USD'.** True today, and wrong the moment a client is billed in ZiG: a figure
 *    would be shown wearing the name of a book it is not in. The code now comes from
 *    {@see CurrencyContextService}, which is the one place the UI learns what the base is. For a figure in
 *    ANOTHER book, use `customCurrency` with that row's own currency — this pipe is for base money only.
 *
 * 2. **It printed a NULL amount as "0.00".** `Number(null).toFixed(2)` is "0.00", and `null == 0` is false in
 *    JavaScript, so a missing figure slipped past the dashes check and came out as a confident zero. That is
 *    the single worst thing a money screen can say: "nothing owed" when the truth is "we do not know". It
 *    happens for real — a balance that has not loaded yet, and, once ZiG is on, any line the API deliberately
 *    returns as `null` because it has no price in the book it is settled in (decision D2). Such a figure now
 *    reads as the same marker the API prints on a document, and never as a number.
 */
@Pipe({
  name: 'defaultCurrency',
})
export class DefaultCurrencyPipe implements PipeTransform {
  constructor(private currency: CurrencyContextService) {}

  transform(
    value: number | null | undefined,
    compact: boolean = false,
    showDashes: boolean = false
  ): string {
    // UNKNOWN is not ZERO. Checked before anything else, and deliberately not with `== 0`.
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return CurrencyContextService.UNAVAILABLE;
    }

    if (value == 0 && showDashes) {
      return '--';
    }

    const code = this.currency.base;

    if (compact) {
      let formatter = Intl.NumberFormat('en', {
        notation: 'compact',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return formatter.format(Number(value)) + ' ' + code;
    }
    return (
      Number(value)
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, '$&,') + ' ' + code
    );
  }
}
